import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import type { ProGroupsProps } from '../types/navigation';
import { ProGroupsClientsView } from './views/ProGroupsClientsView';
import { ProGroupsAccountView } from './views/ProGroupsAccountView';
import { ProGroupsStatsView } from './views/ProGroupsStatsView';
import { ProGroupsAdminView } from './views/ProGroupsAdminView';
import { ProGroupsBuilderView } from './views/ProGroupsBuilderView';
import { DebugView } from './views/DebugView';
import { useContentStore } from '../contexts/StoreContext';
import { SkinFeaturesContext } from '../contexts/SkinFeaturesContext';
import { createcontentService } from '../services/ContentService';
import { resolveSraMenuVisibility, type SraResolvedMenuVisibility } from '../services/account/SraMenuAccess';
import { AccountPracInfoStorage } from '../storage/AccountPracInfoStorage';
import { AccountPracInfoService } from '../services/account/AccountPracInfoService';
import type { AccountPracInfoRequestDto } from '../types/api/service';
import { resolveSkinChannelInfo, type SkinChannelInfo } from '../services/skin/resolveSkinChannelInfo';
import type { PersistedNavigationState } from '../services/progroups';
import {
  ProGroupsMainMenu,
  ProGroupsSubmenuBar,
  resolveInitialSection,
  SECTION_SUBTABS,
  type SectionKey,
} from './progroups-shell';
import { resolveAuthContext } from '../services/auth/authContext';
import { resolveAccountId, resolveStoredSessionAccountId, resolveAccountIdWithRetry } from '../services/auth/resolveAccountId';
import { isDevelopment } from '../config/configManager';

const isDevelopmentMode = (): boolean => {
  return typeof __DEV__ !== 'undefined' ? __DEV__ : isDevelopment();
};

const defaultSubsectionBySection: Record<SectionKey, string> = {
  Debug: SECTION_SUBTABS.Debug[0],
  Admin: SECTION_SUBTABS.Admin[0],
  Builder: SECTION_SUBTABS.Builder[0],
  Clients: SECTION_SUBTABS.Clients[0],
  Stats: SECTION_SUBTABS.Stats[0],
  Account: 'Account Settings',
};

const SIGN_IN_RESET_SUBSECTION_BY_SECTION: Record<SectionKey, string> = {
  Debug: SECTION_SUBTABS.Debug[0],
  Admin: SECTION_SUBTABS.Admin[0],
  Builder: SECTION_SUBTABS.Builder[0],
  Clients: 'Recent Reports',
  Stats: 'Usage',
  Account: 'Account Settings',
};

const RESET_NAVIGATION_ON_SIGN_IN_KEY = 'progroups_navigation_reset_on_signin';
const AUTH_ACCESS_TYPE_KEY = 'auth_access_type';
const RESET_NAVIGATION_ACCESS_TYPES = new Set(['10', '11']);

type BootstrapStatus = 'idle' | 'loading' | 'ready' | 'error';

type BootReadyStatus = 'waiting' | 'ready' | 'error';

const ACCOUNT_SETTINGS_CACHE_WAIT_TIMEOUT_MS = 1500;
const ACCOUNT_SETTINGS_CACHE_POLL_MS = 150;

interface AccountSettingsBootstrapState {
  accountId: string | null;
  status: BootstrapStatus;
  error: string | null;
}

const resolveUserStore = (): any => resolveAuthContext().userStore;

const getCurrentAccessTypeNumber = (): number => {
  const userStore = resolveUserStore();
  const state = userStore?.getState ? userStore.getState() : null;
  const user = state?.session?.user as Record<string, unknown> | undefined;
  
  const accessTypeStr =
    (typeof user?.accessType === 'string' ? user.accessType : null)
    || (typeof user?.AccessType === 'string' ? user.AccessType : null)
    || null;
  
  const parsed = Number.parseInt((accessTypeStr || '').trim(), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveCurrentAccessType = async (storage?: any): Promise<string | null> => {
  const userStore = resolveUserStore();
  const state = userStore?.getState ? userStore.getState() : null;
  const user = state?.session?.user as Record<string, unknown> | undefined;

  const fromStore =
    (typeof user?.accessType === 'string' ? user.accessType : null)
    || (typeof user?.AccessType === 'string' ? user.AccessType : null)
    || null;

  if (fromStore && fromStore.trim().length > 0) {
    return fromStore.trim();
  }

  if (!storage?.getItem) {
    return null;
  }

  try {
    const fromStorage = await storage.getItem(AUTH_ACCESS_TYPE_KEY);
    if (typeof fromStorage === 'string' && fromStorage.trim().length > 0) {
      return fromStorage.trim();
    }
  } catch (error) {
    console.warn('ProGroupsShell: Failed to read access type from storage', error);
  }

  return null;
};

const shouldResetNavigationOnSignIn = async (storage?: any): Promise<boolean> => {
  if (!storage?.getItem) {
    return false;
  }

  try {
    const marker = await storage.getItem(RESET_NAVIGATION_ON_SIGN_IN_KEY);
    return marker === '1';
  } catch (error) {
    console.warn('ProGroupsShell: Failed to read sign-in navigation reset marker', error);
    return false;
  }
};

const consumeNavigationResetMarker = async (storage?: any): Promise<void> => {
  if (!storage?.removeItem) {
    return;
  }

  try {
    await storage.removeItem(RESET_NAVIGATION_ON_SIGN_IN_KEY);
  } catch (error) {
    console.warn('ProGroupsShell: Failed to clear sign-in navigation reset marker', error);
  }
};

const getAccountInFlightRegistry = (): Record<string, boolean> => {
  const globalScope = globalThis as any;
  if (!globalScope.__proGroupsAccountPracInfoInFlightByAccountId) {
    globalScope.__proGroupsAccountPracInfoInFlightByAccountId = {};
  }
  return globalScope.__proGroupsAccountPracInfoInFlightByAccountId as Record<string, boolean>;
};

const getAccountBootstrapRegistry = (): Record<string, boolean> => {
  const globalScope = globalThis as any;
  if (!globalScope.__proGroupsAccountSettingsBootstrappedByAccountId) {
    globalScope.__proGroupsAccountSettingsBootstrappedByAccountId = {};
  }
  return globalScope.__proGroupsAccountSettingsBootstrappedByAccountId as Record<string, boolean>;
};

const isAccountFetchInFlight = (accountId: string): boolean => {
  return !!getAccountInFlightRegistry()[accountId];
};

const setAccountFetchInFlight = (accountId: string, value: boolean): void => {
  getAccountInFlightRegistry()[accountId] = value;
};

const hasBootstrappedAccountSettings = (accountId: string): boolean => {
  return !!getAccountBootstrapRegistry()[accountId];
};

const markAccountSettingsBootstrapped = (accountId: string): void => {
  getAccountBootstrapRegistry()[accountId] = true;
};

const wait = (ms: number): Promise<void> => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const hasAccountSettingsPayload = (payload: unknown): boolean => {
  return !!(payload as any)?.AccountSettings;
};

const isSectionKey = (value: string): value is SectionKey => {
  return value === 'Debug' || value === 'Admin' || value === 'Builder' || value === 'Clients' || value === 'Stats' || value === 'Account';
};

const getDefaultSraMenuVisibility = (): SraResolvedMenuVisibility => {
  const accessType = getCurrentAccessTypeNumber();
  return resolveSraMenuVisibility(undefined, accessType);
};

const getVisibleSubtabsForSection = (
  section: SectionKey,
  sraVisibility: SraResolvedMenuVisibility
): string[] => {
  if (section === 'Debug') {
    return SECTION_SUBTABS.Debug;
  }
  // Admin/Builder are new admin-console-only sections with no backend SRA config settings yet —
  // like Debug, they bypass resolveSraMenuVisibility's per-account subtabsBySection lookup
  // entirely rather than needing new enable_* settings wired up for placeholder tabs.
  if (section === 'Admin') {
    return SECTION_SUBTABS.Admin;
  }
  if (section === 'Builder') {
    return SECTION_SUBTABS.Builder;
  }

  const tabs = sraVisibility.subtabsBySection[section];
  if (tabs.length > 0) {
    return tabs;
  }

  return SECTION_SUBTABS[section];
};

const resolveVisibleSubsection = (
  section: SectionKey,
  requested: string,
  sraVisibility: SraResolvedMenuVisibility
): string => {
  const tabs = getVisibleSubtabsForSection(section, sraVisibility);
  return tabs.includes(requested) ? requested : tabs[0];
};

export const ProGroupsShell: React.FC<ProGroupsProps> = ({
  initialTab,
  storage,
  apiService,
  clientCacheStorage,
  navigationStateStorage,
  onSignOut,
  onViewReport,
}) => {
  const contentStoreHook = useContentStore();
  const contentService = useMemo(() => {
    if (!apiService || !contentStoreHook) {
      return null;
    }
    return createcontentService(apiService as any, contentStoreHook.getState());
  }, [apiService, contentStoreHook]);
  const showDebugTab = isDevelopmentMode();

  const [activeSection, setActiveSection] = useState<SectionKey>('Account');
  const [subsectionBySection, setSubsectionBySection] = useState<Record<SectionKey, string>>(defaultSubsectionBySection);
  const [isNavigationHydrated, setIsNavigationHydrated] = useState(false);
  const [bootstrapRetryCount, setBootstrapRetryCount] = useState(0);
  const [accountSettingsBootstrap, setAccountSettingsBootstrap] = useState<AccountSettingsBootstrapState>({
    accountId: null,
    status: 'idle',
    error: null,
  });
  const [bootReadyStatus, setBootReadyStatus] = useState<BootReadyStatus>('waiting');
  const [isUsingDefaultAccountFlags, setIsUsingDefaultAccountFlags] = useState(false);
  const [sraMenuVisibility, setSraMenuVisibility] = useState<SraResolvedMenuVisibility>(getDefaultSraMenuVisibility);
  const [sraVisibilityRefreshToken, setSraVisibilityRefreshToken] = useState(0);
  const [skinChannelInfo, setSkinChannelInfo] = useState<SkinChannelInfo | null>(null);
  // The active account's own SRA AccessType (10 = Pro, 11+ = Groups) — see SkinFeaturesContext's
  // isProAccount doc comment for why this must be the account's record, not the session's.
  const [isProAccount, setIsProAccount] = useState(false);
  // Account feature flags for deep Clients components (see SkinFeaturesContext) — memoized so
  // the provider value only changes when a flag actually does.
  const enableReportPdf = !!skinChannelInfo?.enableReportPdf;
  const skinFeatures = useMemo(() => ({ enableReportPdf, isProAccount }), [enableReportPdf, isProAccount]);
  const hasLoadedNavigationRef = useRef(false);

  const visibleSections = useMemo(() => {
    const next: SectionKey[] = [];

    if (showDebugTab) {
      next.push('Debug');
    }

    // Admin/Builder are always visible — unlike Clients/Stats/Account, they aren't gated by
    // per-account SRA config settings (see getVisibleSubtabsForSection).
    next.push('Admin', 'Builder');

    (['Clients', 'Stats', 'Account'] as const).forEach((section) => {
      if (sraMenuVisibility.mainMenuEnabled[section]) {
        next.push(section);
      }
    });

    if (!showDebugTab && next.length === 0) {
      return ['Clients' as SectionKey];
    }

    return next;
  }, [showDebugTab, sraMenuVisibility]);

  const initialSection = useMemo(() => resolveInitialSection(initialTab, visibleSections), [initialTab, visibleSections]);

  const resolvedSubsection = useMemo(
    () => resolveVisibleSubsection(activeSection, subsectionBySection[activeSection], sraMenuVisibility),
    [activeSection, sraMenuVisibility, subsectionBySection]
  );

  useEffect(() => {
    let cancelled = false;

    const waitForBootSequence = async () => {
      setBootReadyStatus('waiting');

      try {
        const globalStores = (globalThis as any).__proGroupsStores ?? (globalThis as any).__childNavStores;
        if (globalStores?.executeBootSequence) {
          await globalStores.executeBootSequence('startup');
        }

        if (!cancelled) {
          setBootReadyStatus('ready');
        }
      } catch (error) {
        if (!cancelled) {
          console.error('ProGroupsShell: boot readiness wait failed', error);
          setBootReadyStatus('error');
        }
      }
    };

    void waitForBootSequence();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadNavigation = async () => {
      if (hasLoadedNavigationRef.current) {
        return;
      }

      const shouldResetForSignIn = await shouldResetNavigationOnSignIn(storage);
      const accessType = await resolveCurrentAccessType(storage);
      const shouldUseSignInResetDefaults = !!(
        shouldResetForSignIn
        && (!accessType || RESET_NAVIGATION_ACCESS_TYPES.has(accessType))
      );

      if (shouldUseSignInResetDefaults) {
        hasLoadedNavigationRef.current = true;
        
        // Use first visible section instead of hardcoding 'Clients'
        const fallbackSection = visibleSections.length > 0
          ? (visibleSections.find(s => s !== 'Debug') || visibleSections[0])
          : 'Clients';
        
        const resetNavigation: PersistedNavigationState = {
          activeSection: fallbackSection,
          subsectionBySection: SIGN_IN_RESET_SUBSECTION_BY_SECTION,
        };

        if (navigationStateStorage) {
          await navigationStateStorage.saveNavigation(resetNavigation);
        }

        setSubsectionBySection(SIGN_IN_RESET_SUBSECTION_BY_SECTION);
        setActiveSection(fallbackSection);
        setIsNavigationHydrated(true);
        await consumeNavigationResetMarker(storage);
        return;
      }

      if (shouldResetForSignIn) {
        await consumeNavigationResetMarker(storage);
      }

      if (!navigationStateStorage) {
        hasLoadedNavigationRef.current = true;
        setIsNavigationHydrated(true);
        return;
      }

      const persisted = await navigationStateStorage.loadNavigation();
      if (cancelled || !persisted) {
        if (!cancelled) {
          hasLoadedNavigationRef.current = true;
          setIsNavigationHydrated(true);
        }
        return;
      }

      const persistedSection = persisted.activeSection;
      const nextSection = visibleSections.includes(persistedSection)
        ? persistedSection
        : initialSection;

      const nextSubsectionBySection: Record<SectionKey, string> = {
        ...defaultSubsectionBySection,
      };

      (Object.keys(nextSubsectionBySection) as SectionKey[]).forEach((section) => {
        const candidate = persisted.subsectionBySection?.[section];
        if (candidate && SECTION_SUBTABS[section].includes(candidate)) {
          nextSubsectionBySection[section] = candidate;
        }
      });

      setSubsectionBySection(nextSubsectionBySection);
      setActiveSection(nextSection);
      hasLoadedNavigationRef.current = true;
      setIsNavigationHydrated(true);
    };

    void loadNavigation();

    return () => {
      cancelled = true;
    };
  }, [initialSection, navigationStateStorage, visibleSections]);

  useEffect(() => {
    if (!showDebugTab && activeSection === 'Debug') {
      // Use first visible non-Debug section
      const fallbackSection = visibleSections.find(s => s !== 'Debug') || 'Account';
      setActiveSection(fallbackSection);
    }
  }, [activeSection, showDebugTab, visibleSections]);

  // Redirect if current section becomes invisible (e.g., SRA user on Clients when Clients is hidden)
  useEffect(() => {
    if (isNavigationHydrated && !visibleSections.includes(activeSection)) {
      const fallbackSection = visibleSections.find(s => s !== 'Debug') || visibleSections[0] || 'Account';
      console.log(`ProGroupsShell: Current section '${activeSection}' is not visible, redirecting to '${fallbackSection}'`);
      setActiveSection(fallbackSection);
    }
  }, [activeSection, visibleSections, isNavigationHydrated]);

  // Deep-link into Account > (subsection) after a redirect-based flow returns to the app
  // (e.g. App.tsx stamping __proGroupsPendingAccountSubsection after a Stripe Checkout /
  // Billing Portal return) — runs after persisted nav state has loaded so it isn't clobbered
  // by that load, and overrides whatever subsection was previously persisted.
  useEffect(() => {
    if (!isNavigationHydrated) {
      return;
    }
    const pendingSubsection = (globalThis as any).__proGroupsPendingAccountSubsection;
    if (pendingSubsection) {
      delete (globalThis as any).__proGroupsPendingAccountSubsection;
      handleNavigateToSectionAndSubsection('Account', pendingSubsection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNavigationHydrated]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
      return;
    }

    const handleSraConfigUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      const accountId = customEvent.detail?.accountId;
      console.log('ProGroupsShell: received progroups:sra-config-updated event for accountId:', accountId);
      setSraVisibilityRefreshToken((current) => {
        const next = current + 1;
        console.log('ProGroupsShell: incrementing sraVisibilityRefreshToken from', current, 'to', next);
        return next;
      });
    };

    window.addEventListener('progroups:sra-config-updated', handleSraConfigUpdated as EventListener);

    return () => {
      window.removeEventListener('progroups:sra-config-updated', handleSraConfigUpdated as EventListener);
    };
  }, []);

  // Listen for active account changes and reload account data
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
      return;
    }

    const handleActiveAccountChanged = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { accountId, accountName, preloaded } = customEvent.detail || {};
      
      if (!accountId) {
        console.warn('ProGroupsShell: active-account-changed event missing accountId');
        return;
      }

      console.log('ProGroupsShell: Active account changed to:', accountId, accountName, 'preloaded:', preloaded);

      // CRITICAL: Clear old account's cached data before loading new account
      const oldAccountId = accountSettingsBootstrap.accountId;
      if (oldAccountId && oldAccountId !== accountId && storage) {
        console.log('ProGroupsShell: Clearing cached data for old account:', oldAccountId);
        
        try {
          // Clear AccountPracInfo storage for old account
          const pracStorage = new AccountPracInfoStorage(storage);
          await pracStorage.clearAccountPracInfo(oldAccountId);
          console.log('ProGroupsShell: Cleared AccountPracInfo for account:', oldAccountId);
        } catch (error) {
          console.warn('ProGroupsShell: Failed to clear AccountPracInfo (non-fatal):', error);
        }
        
        try {
          // Clear all UI state tabs (contains account-specific view cache)
          if (clientCacheStorage?.clearAllTabs) {
            await clientCacheStorage.clearAllTabs();
            console.log('ProGroupsShell: Cleared all UI state tabs');
          }
        } catch (error) {
          console.warn('ProGroupsShell: Failed to clear UI state tabs (non-fatal):', error);
        }
      }

      // If data was already preloaded (e.g., from ManageAccountsView), just refresh UI
      if (preloaded) {
        console.log('ProGroupsShell: Account data was preloaded, refreshing UI');
        setSraVisibilityRefreshToken((current) => current + 1);
        setAccountSettingsBootstrap({
          accountId,
          status: 'ready',
          error: null,
        });
        return;
      }

      // Otherwise, fetch all account data for the new account
      if (!apiService || !storage) {
        console.warn('ProGroupsShell: Cannot load account data - apiService or storage missing');
        return;
      }

      try {
        setAccountSettingsBootstrap({
          accountId,
          status: 'loading',
          error: null,
        });

        console.log('ProGroupsShell: Fetching all account data for accountId:', accountId);

        // Fetch ALL account types at once
        const accountService = new AccountPracInfoService(apiService);
        const request: AccountPracInfoRequestDto = {
          AccountId: accountId,
          Types: ['Subscriptions', 'AccountSettings', 'Touchpoints', 'AdminOptions', 'AccountSras'],
        };

        const userStore = resolveUserStore();
        const accountData = await accountService.getAccountPracInfo(request, {
          userStore,
          storage,
        });

        // Save to storage
        const pracStorage = new AccountPracInfoStorage(storage);
        await pracStorage.saveAccountPracInfo(accountId, accountData);

        console.log('ProGroupsShell: Successfully loaded and cached account data for:', accountId);

        // Update UI state
        setAccountSettingsBootstrap({
          accountId,
          status: 'ready',
          error: null,
        });

        // Refresh SRA menu visibility
        setSraVisibilityRefreshToken((current) => current + 1);

        // Dispatch event to confirm account data is ready
        window.dispatchEvent(new CustomEvent('progroups:account-data-loaded', {
          detail: { accountId, accountName },
        }));
      } catch (error: any) {
        console.error('ProGroupsShell: Failed to load account data for', accountId, error);
        setAccountSettingsBootstrap({
          accountId,
          status: 'error',
          error: error?.message || 'Failed to load account data',
        });
      }
    };

    window.addEventListener('progroups:active-account-changed', handleActiveAccountChanged as EventListener);

    return () => {
      window.removeEventListener('progroups:active-account-changed', handleActiveAccountChanged as EventListener);
    };
  }, [apiService, storage]);

  // Once the active account's PracInfo is cached, resolve its SkinTypeDef ChannelInfo
  // (logo/background images, font/bg colors) to style the main menu header bar.
  useEffect(() => {
    if (accountSettingsBootstrap.status !== 'ready' || !storage || !accountSettingsBootstrap.accountId) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const pracStorage = new AccountPracInfoStorage(storage);
        const cached = await pracStorage.getAccountPracInfo(accountSettingsBootstrap.accountId!);
        if (cancelled) {
          return;
        }

        const sraAccessType = Number.parseInt((cached?.SraUser?.AccessType || '').trim(), 10);
        setIsProAccount(sraAccessType === 10);

        const skinType = cached?.AccountSettings?.PracInfo?.SkinType;
        if (!skinType) {
          return;
        }

        const resolved = await resolveSkinChannelInfo(storage, skinType);
        if (!cancelled) {
          setSkinChannelInfo(resolved);
        }
      } catch (error) {
        console.warn('ProGroupsShell: failed to resolve skin channel info', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountSettingsBootstrap.accountId, accountSettingsBootstrap.status, storage]);

  useEffect(() => {
    if (visibleSections.includes(activeSection)) {
      return;
    }

    const fallbackSection: SectionKey = visibleSections[0] ?? 'Clients';
    setActiveSection(fallbackSection);
  }, [activeSection, visibleSections]);

  useEffect(() => {
    if (!isNavigationHydrated || !navigationStateStorage) {
      return;
    }

    const validSubsections: Record<SectionKey, string> = {
      Debug: resolveVisibleSubsection('Debug', subsectionBySection.Debug, sraMenuVisibility),
      Admin: resolveVisibleSubsection('Admin', subsectionBySection.Admin, sraMenuVisibility),
      Builder: resolveVisibleSubsection('Builder', subsectionBySection.Builder, sraMenuVisibility),
      Clients: resolveVisibleSubsection('Clients', subsectionBySection.Clients, sraMenuVisibility),
      Stats: resolveVisibleSubsection('Stats', subsectionBySection.Stats, sraMenuVisibility),
      Account: resolveVisibleSubsection('Account', subsectionBySection.Account, sraMenuVisibility),
    };

    const next: PersistedNavigationState = {
      activeSection,
      subsectionBySection: validSubsections,
    };

    void navigationStateStorage.saveNavigation(next);
  }, [activeSection, isNavigationHydrated, navigationStateStorage, sraMenuVisibility, subsectionBySection]);

  useEffect(() => {
    let cancelled = false;

    const hydrateSraMenuVisibility = async () => {
      if (accountSettingsBootstrap.status !== 'ready') {
        return;
      }

      const accountId = accountSettingsBootstrap.accountId;
      if (!accountId || !storage) {
        setSraMenuVisibility(getDefaultSraMenuVisibility());
        return;
      }

      try {
        const pracStorage = new AccountPracInfoStorage(storage);
        const cached = await pracStorage.getAccountPracInfo(accountId);
        if (cancelled) {
          return;
        }

        const settings = cached?.SraUser?.ConfigInfo;
        const accessType = getCurrentAccessTypeNumber();
        const parsedSraAccessType = Number.parseInt((cached?.SraUser?.AccessType || '').trim(), 10);
        setSraMenuVisibility(resolveSraMenuVisibility(
          settings,
          accessType,
          Number.isFinite(parsedSraAccessType) ? parsedSraAccessType : undefined
        ));
      } catch (error) {
        if (!cancelled) {
          console.warn('ProGroupsShell: failed to read SRA menu visibility, using defaults', error);
          setSraMenuVisibility(getDefaultSraMenuVisibility());
        }
      }
    };

    void hydrateSraMenuVisibility();

    return () => {
      cancelled = true;
    };
  }, [accountSettingsBootstrap.accountId, accountSettingsBootstrap.status, sraVisibilityRefreshToken, storage]);

  useEffect(() => {
    let cancelled = false;

    const bootstrapAccountSettings = async () => {
      if (!isNavigationHydrated || bootReadyStatus === 'waiting') {
        return;
      }

      if (bootReadyStatus === 'error') {
        setAccountSettingsBootstrap({
          accountId: null,
          status: 'error',
          error: 'Boot sequence failed. Please retry sign-in.',
        });
        return;
      }

      const accountId = await resolveAccountIdWithRetry(storage);
      if (!accountId) {
        setIsUsingDefaultAccountFlags(false);
        setAccountSettingsBootstrap({
          accountId: null,
          status: 'error',
          error: 'Could not resolve account id for account settings bootstrap. Login response should include User.AccountId.',
        });
        return;
      }

      setAccountSettingsBootstrap((current) => {
        if (current.accountId === accountId && current.status === 'ready') {
          return current;
        }

        return {
          accountId,
          status: 'loading',
          error: null,
        };
      });

      if (hasBootstrappedAccountSettings(accountId)) {
        setAccountSettingsBootstrap({
          accountId,
          status: 'ready',
          error: null,
        });
        return;
      }

      const pracStorage = storage ? new AccountPracInfoStorage(storage) : null;
      if (!pracStorage) {
        setAccountSettingsBootstrap({
          accountId,
          status: 'error',
          error: 'Storage is unavailable for account settings bootstrap.',
        });
        return;
      }

      const cached = await pracStorage.getAccountPracInfo(accountId);
      if (!cancelled && cached && hasAccountSettingsPayload(cached)) {
        setIsUsingDefaultAccountFlags(false);
        markAccountSettingsBootstrapped(accountId);
        setAccountSettingsBootstrap({
          accountId,
          status: 'ready',
          error: null,
        });
        return;
      }

      if (isAccountFetchInFlight(accountId)) {
        for (let attempt = 0; attempt < 15; attempt += 1) {
          if (cancelled) {
            return;
          }

          await wait(200);
          if (!isAccountFetchInFlight(accountId)) {
            break;
          }
        }

        const cachedAfterWait = await pracStorage.getAccountPracInfo(accountId);
        if (!cancelled && cachedAfterWait && hasAccountSettingsPayload(cachedAfterWait)) {
          setIsUsingDefaultAccountFlags(false);
          markAccountSettingsBootstrapped(accountId);
          setAccountSettingsBootstrap({
            accountId,
            status: 'ready',
            error: null,
          });
          return;
        }
      }

      const deadline = Date.now() + ACCOUNT_SETTINGS_CACHE_WAIT_TIMEOUT_MS;
      while (!cancelled && Date.now() < deadline) {
        await wait(ACCOUNT_SETTINGS_CACHE_POLL_MS);

        const delayedCache = await pracStorage.getAccountPracInfo(accountId);
        if (delayedCache && hasAccountSettingsPayload(delayedCache)) {
          setIsUsingDefaultAccountFlags(false);
          markAccountSettingsBootstrapped(accountId);
          setAccountSettingsBootstrap({
            accountId,
            status: 'ready',
            error: null,
          });
          return;
        }
      }

      setIsUsingDefaultAccountFlags(true);
      setAccountSettingsBootstrap({
        accountId,
        status: 'error',
        error: 'Account settings cache is delayed. Rendering Clients with default feature flags.',
      });
    };

    void bootstrapAccountSettings();

    return () => {
      cancelled = true;
    };
  }, [bootReadyStatus, bootstrapRetryCount, isNavigationHydrated, storage]);

  const handleSectionChange = (nextSection: SectionKey) => {
    const tabs = getVisibleSubtabsForSection(nextSection, sraMenuVisibility);
    const forcedInitialSubsection = nextSection === 'Stats'
      ? 'Usage'
      : nextSection === 'Account'
        ? 'Account Settings'
        : null;

    setActiveSection(nextSection);
    setSubsectionBySection((current) => ({
      ...current,
      [nextSection]: resolveVisibleSubsection(
        nextSection,
        forcedInitialSubsection || current[nextSection] || tabs[0],
        sraMenuVisibility
      ),
    }));
  };

  const handleSubsectionChange = (nextSubsection: string) => {
    setSubsectionBySection((current) => ({
      ...current,
      [activeSection]: resolveVisibleSubsection(activeSection, nextSubsection, sraMenuVisibility),
    }));
  };

  const handleNavigateToSectionAndSubsection = (section: SectionKey, subsection: string) => {
    setActiveSection(section);
    setSubsectionBySection((current) => ({
      ...current,
      [section]: resolveVisibleSubsection(section, subsection, sraMenuVisibility),
    }));
  };

  const handleDebugBack = () => {
    handleSectionChange('Clients');
  };

  const handleSignOut = async () => {
    if (storage?.setItem) {
      try {
        await storage.setItem(RESET_NAVIGATION_ON_SIGN_IN_KEY, '1');
      } catch (error) {
        console.warn('ProGroupsShell: Failed to set sign-in navigation reset marker on sign-out', error);
      }
    }

    if (onSignOut) {
      await onSignOut();
    }
  };

  return (
    <SkinFeaturesContext.Provider value={skinFeatures}>
    <View style={styles.shell}>
      <ProGroupsMainMenu
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        onSignOut={() => { void handleSignOut(); }}
        showDebugTab={showDebugTab}
        visibleSections={visibleSections}
        logoSrc={skinChannelInfo?.logoSrc}
        backgroundSrc={skinChannelInfo?.backgroundSrc}
        menuFontColor={skinChannelInfo?.menuFontColor}
        skinBgColor={skinChannelInfo?.skinBgColor}
      />

      <ProGroupsSubmenuBar
        activeSection={activeSection}
        activeSubsection={resolvedSubsection}
        onSubsectionChange={handleSubsectionChange}
        onSectionChange={handleSectionChange}
        onNavigateToSectionAndSubsection={handleNavigateToSectionAndSubsection}
        subtabs={getVisibleSubtabsForSection(activeSection, sraMenuVisibility)}
        storage={storage}
      />

      <View style={styles.contentArea}>
        {activeSection === 'Clients' ? (
          accountSettingsBootstrap.status === 'ready' || isUsingDefaultAccountFlags ? (
            <>
              {isUsingDefaultAccountFlags ? (
                <View style={styles.nonBlockingNotice}>
                  <Text style={styles.nonBlockingNoticeText}>
                    Account settings are still loading. Clients is using default feature flags temporarily.
                  </Text>
                </View>
              ) : null}
              <ProGroupsClientsView
                storage={storage}
                apiService={apiService}
                clientCacheStorage={clientCacheStorage}
                initialSubsection={resolvedSubsection}
                onSubsectionChange={handleSubsectionChange}
                onViewReport={onViewReport}
              />
            </>
          ) : (
            <View style={styles.placeholder}>
              <Text style={styles.placeholderTitle}>Loading Account Settings</Text>
              <Text style={styles.placeholderText}>
                Clients waits for practice account settings so feature flags are applied correctly.
              </Text>
              {accountSettingsBootstrap.status === 'error' ? (
                <>
                  <Text style={styles.bootstrapErrorText}>{accountSettingsBootstrap.error || 'Unable to load account settings.'}</Text>
                  <Pressable style={styles.retryButton} onPress={() => setBootstrapRetryCount((current) => current + 1)}>
                    <Text style={styles.retryButtonText}>Retry</Text>
                  </Pressable>
                </>
              ) : null}
            </View>
          )
        ) : activeSection === 'Account' ? (
          <ProGroupsAccountView
            key={`account-${accountSettingsBootstrap.accountId}-${sraVisibilityRefreshToken}`}
            storage={storage}
            apiService={apiService}
            clientCacheStorage={clientCacheStorage}
            initialSubsection={resolvedSubsection}
          />
        ) : activeSection === 'Stats' ? (
          <ProGroupsStatsView
            storage={storage}
            apiService={apiService}
            initialSubsection={resolvedSubsection}
          />
        ) : activeSection === 'Admin' ? (
          <ProGroupsAdminView
            storage={storage}
            apiService={apiService}
            initialSubsection={resolvedSubsection}
          />
        ) : activeSection === 'Builder' ? (
          <ProGroupsBuilderView
            storage={storage}
            apiService={apiService}
            initialSubsection={resolvedSubsection}
          />
        ) : activeSection === 'Debug' ? (
          <DebugView
            onBack={handleDebugBack}
            storage={storage}
            apiService={apiService}
            contentService={contentService ?? undefined}
            useContentStore={contentStoreHook}
            onSignOut={onSignOut}
          />
        ) : (
          <View style={styles.placeholder}>
            <Text style={styles.placeholderTitle}>{activeSection}</Text>
            <Text style={styles.placeholderText}>This section is queued for migration after the Clients slice.</Text>
          </View>
        )}
      </View>
    </View>
    </SkinFeaturesContext.Provider>
  );
};

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  contentArea: {
    flex: 1,
  },
  nonBlockingNotice: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#fff7ed',
    borderBottomWidth: 1,
    borderBottomColor: '#fed7aa',
  },
  nonBlockingNoticeText: {
    fontSize: 12,
    lineHeight: 18,
    color: '#9a3412',
    textAlign: 'center',
  },
  placeholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  placeholderTitle: {
    fontSize: 26,
    fontWeight: '800',
    color: '#0f172a',
    marginBottom: 8,
  },
  placeholderText: {
    fontSize: 14,
    lineHeight: 22,
    color: '#475569',
    textAlign: 'center',
    maxWidth: 480,
  },
  bootstrapErrorText: {
    marginTop: 10,
    fontSize: 13,
    lineHeight: 20,
    color: '#b91c1c',
    textAlign: 'center',
    maxWidth: 560,
  },
  retryButton: {
    marginTop: 14,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: '#ffffff',
  },
  retryButtonText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0f172a',
  },
});
